import type { LeaveBalanceRow } from "@/features/leave/schemas/leave.schema";

/**
 * The decision this module exists to encode (plan decision 2, settled with
 * Rajat 2026-08-18 against a six-product survey): the balance card leads with
 * `available`, one large figure. `consumable` appears only when it is lower,
 * phrased as what can actually be spent and by when. Provenance (`credited`,
 * `carried`, `taken`, `fresh`, `allocationId`) never reaches this shape at
 * all, because `credited` already includes carried-forward days and showing
 * it beside `available` double-counts and reads as a bigger grant than the
 * person received. Provenance belongs on the ledger (Task 3), never the card.
 *
 * Kept pure and DOM-free on purpose: Tasks 6 and 7 consume `describeBalance`
 * directly, and the decision is far easier to get right in a plain function
 * than inside a render tree.
 */

export interface BalanceWarning {
  /** What is actually safe to spend, i.e. `consumable`. Always present when
   *  the warning exists: consumable is always lower than available here. */
  spendable: string;
  /** The carry-forward lapse date, when that is the reason consumable is
   *  lower. `consumable` can also be capped by the allocation's own end date,
   *  which has no date to report, so this is null rather than a guess: a
   *  warning that names no date is honest, a warning with a made-up date is
   *  not. */
  by: string | null;
}

export interface BalanceDisplay {
  leaveTypeId: string;
  leaveTypeName: string;
  /** The headline figure: `available`. */
  headline: string;
  /** Days on undecided applications, or null when there are none. Suppressed
   *  at zero rather than shown as "0 pending", since a zero pending count is
   *  not something worth a person's attention. */
  pending: string | null;
  /** Set only when `consumable` is strictly lower than `available`; null in
   *  the common case where they match (the research survey's own finding: no
   *  surveyed HRMS product warns proactively, and suppressing the equal case
   *  is what keeps this warning meaningful when it does appear). */
  warning: BalanceWarning | null;
}

/**
 * Compares two `Decimal(9,4)` strings without ever parsing them to a number.
 *
 * Two ways to get this wrong, both live bugs in this feature if taken:
 * naive string comparison (`a < b`) is lexicographic, so `"9" < "10"` is
 * FALSE (`'9'` sorts after `'1'` by character code) even though 9 is
 * smaller; and `parseFloat` round-trips through IEEE 754, reintroducing the
 * precision drift `Decimal` exists to prevent, on the one figure in the
 * whole module where the number shown has to be the number the gate
 * enforced.
 *
 * The fix: split each value into a sign, an integer part, and a fractional
 * part, and compare those three independently. Same-length digit strings
 * compare correctly with `<`/`>` because that is exactly what lexicographic
 * order means for a fixed-width base-10 number, so the only work is making
 * the widths match first: pad integer parts by length before comparing, and
 * pad fractional parts with trailing zeros so "5" (tenths) compares against
 * "25" (hundredths) as "50" vs "25", not "5" vs "25".
 *
 * Returns a negative number when `a < b`, positive when `a > b`, zero when
 * equal, matching `Array.prototype.sort`'s comparator contract.
 */
export function compareDecimalStrings(a: string, b: string): number {
  const left = splitSign(a);
  const right = splitSign(b);
  if (left.negative !== right.negative) return left.negative ? -1 : 1;
  const magnitude = compareMagnitudes(left.magnitude, right.magnitude);
  return left.negative ? -magnitude : magnitude;
}

function splitSign(value: string): { negative: boolean; magnitude: string } {
  if (value.startsWith("-")) return { negative: true, magnitude: value.slice(1) };
  return { negative: false, magnitude: value };
}

function compareMagnitudes(a: string, b: string): number {
  const [aIntRaw, aFracRaw] = a.split(".");
  const [bIntRaw, bFracRaw] = b.split(".");
  const aInt = trimLeadingZeros(aIntRaw ?? "0");
  const bInt = trimLeadingZeros(bIntRaw ?? "0");

  if (aInt.length !== bInt.length) return aInt.length - bInt.length;
  if (aInt !== bInt) return aInt < bInt ? -1 : 1;

  const fracLength = Math.max(aFracRaw?.length ?? 0, bFracRaw?.length ?? 0);
  const aFrac = (aFracRaw ?? "").padEnd(fracLength, "0");
  const bFrac = (bFracRaw ?? "").padEnd(fracLength, "0");
  if (aFrac === bFrac) return 0;
  return aFrac < bFrac ? -1 : 1;
}

function trimLeadingZeros(digits: string): string {
  const trimmed = digits.replace(/^0+(?=\d)/, "");
  return trimmed === "" ? "0" : trimmed;
}

function isZero(value: string): boolean {
  return compareDecimalStrings(value, "0") === 0;
}

/// Only what a `Decimal(9,4)` can carry over the wire. Anything else is not a
/// figure this module knows how to add.
const DECIMAL_STRING = /^-?\d*(\.\d*)?$/;

/**
 * Adds `Decimal(9,4)` strings exactly, the same no-float discipline
 * `compareDecimalStrings` keeps for the same reason.
 *
 * The stats section sums `taken`, `available` and `pending` across leave
 * types, and those sums sit on the same page as the per-type figures they are
 * built from. `Number("0.1") + Number("0.2")` is `0.30000000000000004`, so a
 * float sum of three half days is how the total ends up disagreeing with the
 * three rows a reader can add up themselves.
 *
 * The method: find the widest fractional part, scale every value to that many
 * digits as a `BigInt`, add, then place the point back. BigInt is exact at any
 * width these figures can reach, which is the whole reason it is used instead
 * of a scaled `number`.
 *
 * Trailing fractional zeros are dropped on the way out ("42.0000" becomes
 * "42"), because these strings are rendered to people rather than compared
 * against the shore's own.
 *
 * A value that is not a plain decimal contributes NOTHING rather than
 * throwing. Zod guarantees these are strings, not that they are numeric, and
 * a person checking their leave balance should not meet a blank page because
 * one figure arrived malformed.
 */
export function addDecimalStrings(values: ReadonlyArray<string>): string {
  let scale = 0;
  for (const value of values) {
    const fraction = value.split(".")[1] ?? "";
    if (fraction.length > scale) scale = fraction.length;
  }
  let total = 0n;
  for (const value of values) total += toScaledInteger(value, scale);
  return fromScaledInteger(total, scale);
}

/// One value normalised for display: "42.0000" becomes "42", "12.5000"
/// becomes "12.5". The shore sends whatever `Decimal.toString()` produced, and
/// four trailing zeros beside the word "days" reads like a precision nobody
/// asked for.
export function trimDecimalString(value: string): string {
  return addDecimalStrings([value]);
}

function toScaledInteger(value: string, scale: number): bigint {
  if (!DECIMAL_STRING.test(value)) return 0n;
  const negative = value.startsWith("-");
  const magnitude = negative ? value.slice(1) : value;
  const [wholeRaw, fractionRaw] = magnitude.split(".");
  const whole = wholeRaw === undefined || wholeRaw === "" ? "0" : wholeRaw;
  const digits = `${whole}${(fractionRaw ?? "").padEnd(scale, "0")}`;
  const parsed = BigInt(digits);
  return negative ? -parsed : parsed;
}

function fromScaledInteger(total: bigint, scale: number): string {
  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? "" : digits.slice(digits.length - scale).replace(/0+$/, "");
  const body = fraction === "" ? whole : `${whole}.${fraction}`;
  return negative && body !== "0" ? `-${body}` : body;
}

export function describeBalance(balance: LeaveBalanceRow): BalanceDisplay {
  const isLower = compareDecimalStrings(balance.consumable, balance.available) < 0;
  return {
    leaveTypeId: balance.leaveTypeId,
    leaveTypeName: balance.leaveTypeName,
    headline: balance.available,
    pending: isZero(balance.pending) ? null : balance.pending,
    warning: isLower ? { spendable: balance.consumable, by: balance.carryForwardExpiresOn } : null,
  };
}
